/**
 * Column Visibility Utilities
 *
 * Shared utilities for column visibility and ordering management.
 * Used by both tasks-datatable and matters-datatable (and future datatables).
 *
 * This is the single source of truth for:
 * 1. URL serialization/parsing for column state
 * 2. Column grouping by category
 */

// ----------------------------------------------------------------------------
// URL Serialization
// ----------------------------------------------------------------------------

/** Default URL parameter key for column state */
export const DEFAULT_COLUMN_URL_PARAM = 'cols';

/**
 * Serialize visible columns to URL param value.
 * Returns null if columns match defaults (for cleaner URLs).
 */
export function serializeColumnsToUrl(
  columns: string[],
  defaultColumns: string[],
): string | null {
  // If same length and same order, no need to persist
  if (columns.length === defaultColumns.length) {
    const isSame = columns.every((col, idx) => col === defaultColumns[idx]);
    if (isSame) return null;
  }
  return columns.join(',');
}

/**
 * Parse columns from URL param.
 * Returns null if param is empty/missing (use defaults).
 */
export function parseColumnsFromUrl(param: string | null): string[] | null {
  if (!param) return null;
  return param.split(',').filter(Boolean);
}

// ----------------------------------------------------------------------------
// Column Grouping
// ----------------------------------------------------------------------------

/**
 * Result of grouping columns by category.
 */
export interface ColumnGroup<TCategory extends string = string> {
  category: TCategory | 'Other';
  columns: string[];
}

/**
 * Create a column grouping function for a specific set of categories.
 *
 * @param categoryMap - Maps column IDs to their category
 * @param categoryOrder - Order in which categories should appear
 * @returns A function that groups column IDs by category
 *
 * @example
 * const groupByCategory = createColumnGrouper(
 *   { name: 'Info', date: 'Dates' },
 *   ['Info', 'Dates']
 * );
 * groupByCategory(['name', 'date', 'other']);
 * // => [{ category: 'Info', columns: ['name'] }, { category: 'Dates', columns: ['date'] }, { category: 'Other', columns: ['other'] }]
 */
export function createColumnGrouper<TCategory extends string>(
  categoryMap: Record<string, TCategory>,
  categoryOrder: readonly TCategory[],
): (columnIds: string[]) => ColumnGroup<TCategory>[] {
  return (columnIds: string[]): ColumnGroup<TCategory>[] => {
    const groups = new Map<TCategory | 'Other', string[]>();

    // Initialize groups in display order
    categoryOrder.forEach((cat) => {
      groups.set(cat, []);
    });
    groups.set('Other', []);

    // Assign columns to groups
    columnIds.forEach((id) => {
      const category = categoryMap[id];
      groups.get(category ?? 'Other')?.push(id);
    });

    // Convert to array, filtering out empty groups
    return Array.from(groups.entries())
      .filter(([, columns]) => columns.length > 0)
      .map(([category, columns]) => ({ category, columns }));
  };
}

// ----------------------------------------------------------------------------
// Display Name Utilities
// ----------------------------------------------------------------------------

/**
 * Create a display name getter function.
 *
 * @param displayNames - Maps column IDs to display names
 * @returns A function that gets the display name for a column ID
 */
export function createDisplayNameGetter(
  displayNames: Record<string, string>,
): (columnId: string) => string {
  return (columnId: string): string => {
    return displayNames[columnId] ?? columnId;
  };
}
